// The scene catalogue (D/bg/final/scenes.json, DESIGN-SYSTEM §7) for <SceneBackdrop> and the per-user background picker. Files live in
// apps/assistant/public/scenic/. WP-THEME. Arabic labels follow scripts/check-locales.mjs (no diacritics) and are pending MoET review.
// The images are AI-generated originals (BACKGROUND §8): MoET Communications signs off on depicting real landmarks before production.

export type SceneId = "econic-dawn" | "econic-night" | "econic-waterfront" | "econic-waterfront-night" | "plain-mist";
export type SceneTheme = "light" | "dark";
export interface SceneText { en: string; ar: string }

export interface SceneFiles {
  /** `file width` pairs, smallest first, relative to SCENE_BASE. */
  avif: readonly string[];
  webp: readonly string[];
  jpg: readonly string[];
  svgFallback?: string;
}

export interface SceneDef {
  id: SceneId;
  theme: SceneTheme | "any";
  /** The same place in the other theme (Dawn ↔ Night). */
  pair?: SceneId;
  label: SceneText;
  description: SceneText;
  files?: SceneFiles;
  objectPosition: string;
  /** A 32 × 18 WebP shown until the photograph decodes. */
  lqip?: string;
  /** For "plain-mist": the gradient per theme. */
  css?: Record<SceneTheme, string>;
  /** Vertical bands (y % ranges) where bare text on the scene measured WCAG AA with the scrim. */
  textSafe: readonly (readonly [number, number])[];
}

export const SCENE_BASE = "/scenic/";

export const SCENES: Readonly<Record<SceneId, SceneDef>> = {
  "econic-dawn": {
    id: "econic-dawn",
    theme: "light",
    pair: "econic-night",
    label: { en: "ECONIC Dawn", ar: "فجر إيكونيك" },
    description: { en: "Dubai skyline at dawn over still water", ar: "أفق دبي عند الفجر فوق مياه ساكنة" },
    files: {
      avif: ["econic-dawn-1280.avif 1280w", "econic-dawn.avif 2560w"],
      webp: ["econic-dawn-1280.webp 1280w", "econic-dawn.webp 2560w"],
      jpg: ["econic-dawn-1280.jpg 1280w", "econic-dawn.jpg 2560w"],
      svgFallback: "econic-dawn.svg",
    },
    objectPosition: "68% 56%",
    lqip: "data:image/webp;base64,UklGRmoAAABXRUJQVlA4IF4AAABwBACdASogABIAPslQn0unpKKhsAwA8BkJZQCdACHw8f3/238TRCaKyqIswAD+6tSlGaYICo1IevhvcKXaFO7dVtSRiKtng8l8A/Bbf7wUJuw7t5CoNGhVQlS+uAAA",
    textSafe: [[0, 100]],
  },
  "econic-night": {
    id: "econic-night",
    theme: "dark",
    pair: "econic-dawn",
    label: { en: "ECONIC Night", ar: "ليل إيكونيك" },
    description: { en: "The same skyline at night, teal and gold lights", ar: "الأفق نفسه ليلا بأضواء فيروزية وذهبية" },
    files: {
      avif: ["econic-night-1280.avif 1280w", "econic-night.avif 2560w"],
      webp: ["econic-night-1280.webp 1280w", "econic-night.webp 2560w"],
      jpg: ["econic-night-1280.jpg 1280w", "econic-night.jpg 2560w"],
      svgFallback: "econic-night.svg",
    },
    objectPosition: "68% 56%",
    lqip: "data:image/webp;base64,UklGRloAAABXRUJQVlA4IE4AAACwAwCdASogABIAPtFYo0uoJSMhsAgBABoJYwC/OCHXWXKV6XxkAAD+8qGFjiBhauBGE7zngym683TKpSFz9Z31cKjN98dK23S4rfiQAAA=",
    textSafe: [[0, 38], [62, 100]],
  },
  "econic-waterfront": {
    id: "econic-waterfront",
    theme: "light",
    pair: "econic-waterfront-night",
    label: { en: "Abu Dhabi waterfront", ar: "واجهة أبوظبي البحرية" },
    description: { en: "Etihad Towers and the Corniche at dawn", ar: "أبراج الاتحاد والكورنيش عند الفجر" },
    files: {
      avif: ["econic-waterfront-1280.avif 1280w", "econic-waterfront.avif 2560w"],
      webp: ["econic-waterfront-1280.webp 1280w", "econic-waterfront.webp 2560w"],
      jpg: ["econic-waterfront-1280.jpg 1280w", "econic-waterfront.jpg 2560w"],
      svgFallback: "econic-dawn.svg",
    },
    objectPosition: "70% 56%",
    lqip: "data:image/webp;base64,UklGRngAAABXRUJQVlA4IGwAAABQBACdASogABIAPtFgqk4oJaQiKAqpABoJYwCuHDTv4A8CiMEjwczXQQoAAP70+X+4xh3eOIEUkM+IooWt27/56DiImS7Nl+AWKDqBRqnrEfXe7raGZfN4+PGY7W/8lz1I+rxZEqFji8nAAAA=",
    textSafe: [[0, 20], [80, 100]],
  },
  "econic-waterfront-night": {
    id: "econic-waterfront-night",
    theme: "dark",
    pair: "econic-waterfront",
    label: { en: "Abu Dhabi waterfront at night", ar: "واجهة أبوظبي البحرية ليلا" },
    description: { en: "Etihad Towers and the Corniche at night", ar: "أبراج الاتحاد والكورنيش ليلا" },
    files: {
      avif: ["econic-waterfront-night-1280.avif 1280w", "econic-waterfront-night.avif 2560w"],
      webp: ["econic-waterfront-night-1280.webp 1280w", "econic-waterfront-night.webp 2560w"],
      jpg: ["econic-waterfront-night-1280.jpg 1280w", "econic-waterfront-night.jpg 2560w"],
      svgFallback: "econic-night.svg",
    },
    objectPosition: "70% 56%",
    lqip: "data:image/webp;base64,UklGRmIAAABXRUJQVlA4IFYAAAAQBACdASogABIAPtFUokuoJKMhsAgBABoJYwC7ACHWTASW0t4nU5uAAAD+8p76f3E6JMEgaJJC0Fsn9sLe5YfNS4QgTHYmO0izV0lJS6t29ZqsjBEAAA==",
    textSafe: [[0, 38], [80, 100]],
  },
  "plain-mist": {
    id: "plain-mist",
    theme: "any",
    label: { en: "Plain mist", ar: "ضباب هادئ" },
    description: { en: "No photograph: the scene gradient only", ar: "بلا صورة: تدرج لوني فقط" },
    objectPosition: "50% 50%",
    css: {
      light: "linear-gradient(180deg,#C0DAED 0%,#E6EFF5 38%,#FDFCF8 58%,#E6EBEE 100%)",
      dark: "linear-gradient(180deg,#011B33 0%,#012441 45%,#023556 100%)",
    },
    textSafe: [[0, 100]],
  },
};

/** The default scene per theme. */
export const DEFAULT_SCENES: Readonly<Record<SceneTheme, SceneId>> = { light: "econic-dawn", dark: "econic-night" };

/** What the background picker offers (light names; dark mode shows each one's night pair). */
export const PICKER_SCENES: readonly SceneId[] = ["econic-dawn", "econic-waterfront", "plain-mist"];

/** The landing crops (/login, /mfa, /password): 1.4× zoom so the Burj stands between the start column and the sign-in card. */
export const LANDING_SCENES: Readonly<Record<SceneTheme, { id: string; files: { avif: string; webp: string; jpg: string }; objectPosition: string; lqip: string }>> = {
  light: {
    id: "econic-dawn-landing",
    files: { avif: "econic-dawn-landing.avif", webp: "econic-dawn-landing.webp", jpg: "econic-dawn-landing.jpg" },
    objectPosition: "59.5% 50%",
    lqip: "data:image/webp;base64,UklGRl4AAABXRUJQVlA4IFIAAADQAwCdASogABIAPsVOoUunpCcisAwA8BiJZQC9WCHhSgva5urmRAgA/u/ITOuqaXE+f/NYresQxg/MEAVMkX/qCQd1IPrhSRwcwN7Vs+qvAAAA",
  },
  dark: {
    id: "econic-night-landing",
    files: { avif: "econic-night-landing.avif", webp: "econic-night-landing.webp", jpg: "econic-night-landing.jpg" },
    objectPosition: "59.5% 50%",
    lqip: "data:image/webp;base64,UklGRmQAAABXRUJQVlA4IFgAAACQBACdASogABIAPs1YoUunpSMhsBgMAPAZiWUAtOgQ7BWwygrypw7GoVAxCAAA/u7Tj2ABC6OrbK0q79FxhKvFlMqWtvfLlHOPyfseAJUMkSOBbiUFMkAA",
  },
};

/** The ribbons overlay of the landing waves and the PDF covers. */
export const RIBBONS_SVG = `${SCENE_BASE}econic-ribbons.svg`;

export function isSceneId(value: unknown): value is SceneId {
  return typeof value === "string" && value in SCENES;
}

/**
 * The scene to draw for a stored preference in the current theme: a light scene in dark mode becomes its night pair (and the reverse);
 * an unknown or missing preference becomes the theme's default.
 */
export function resolveScene(preference: string | null | undefined, theme: SceneTheme): SceneDef {
  const pref = isSceneId(preference) ? SCENES[preference] : undefined;
  if (!pref) return SCENES[DEFAULT_SCENES[theme]];
  if (pref.theme === "any" || pref.theme === theme) return pref;
  return pref.pair ? SCENES[pref.pair] : SCENES[DEFAULT_SCENES[theme]];
}

/** `srcset` text for one format of a scene. */
export function sceneSrcSet(files: readonly string[]): string {
  return files.map((f) => `${SCENE_BASE}${f}`).join(", ");
}

/** The largest file of a format (the `src` of the <img>). */
export function sceneSrc(files: readonly string[]): string {
  const last = files[files.length - 1] ?? "";
  return `${SCENE_BASE}${last.split(" ")[0]}`;
}
